import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ClipboardList, Workflow, Zap, Link2, BarChart3,
  ArrowRight, CheckCircle, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';

const capabilities = [
  {
    icon: Workflow,
    title: 'Workflow Design',
    description: 'Design and implement efficient claims workflows that reduce cycle times and improve adjuster productivity.',
    features: [
      'Custom workflow configuration',
      'Assignment rules',
      'Workload balancing',
      'SLA management',
    ],
  },
  {
    icon: Brain,
    title: 'Automation & Smart Decisions',
    description: 'Leverage automation and AI-powered decisions to streamline claims processing and reduce manual intervention.',
    features: [
      'Auto-adjudication rules',
      'Fraud detection integration',
      'Smart claim segmentation',
      'Predictive analytics',
    ],
  },
  {
    icon: Link2,
    title: 'Third Party Integrations',
    description: 'Connect ClaimCenter with external service providers, vendors, and data sources for seamless claims handling.',
    features: [
      'Vendor management',
      'ISO ClaimSearch',
      'Medical provider networks',
      'Subrogation services',
    ],
  },
  {
    icon: BarChart3,
    title: 'Reporting & Analytics',
    description: 'Gain insights into claims performance with comprehensive reporting and real-time analytics dashboards.',
    features: [
      'Custom dashboards',
      'Performance metrics',
      'Trend analysis',
      'Executive reporting',
    ],
  },
];

const claimsProcess = [
  { step: 'FNOL', description: 'First Notice of Loss capture and triage' },
  { step: 'Assignment', description: 'Intelligent claim assignment to adjusters' },
  { step: 'Investigation', description: 'Evidence gathering and analysis' },
  { step: 'Evaluation', description: 'Coverage and liability determination' },
  { step: 'Settlement', description: 'Payment processing and closure' },
];

const ClaimCenter = () => {
  return (
    <div data-testid="claimcenter-page">
      <SEO {...pageSEO.claimCenter} />
      <PageHeader
        label="Core Platform"
        title="Claims Engineering & Automation"
        description="Transform your claims operations with intelligent workflows, automation, and seamless third-party integrations."
      />

      {/* Overview Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-sky-600" />
                </div>
                <span className="text-sky-600 font-semibold">ClaimCenter</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
                Accelerate Claims Resolution
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                In today's competitive insurance landscape, claims experience defines 
                customer loyalty. Our ClaimCenter expertise helps you deliver faster, 
                more accurate claims handling.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                From FNOL to settlement, we configure ClaimCenter to optimize every 
                step of the claims journey with intelligent automation and 
                data-driven decisions.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  Discuss Your Project
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              {/* Claims Process Flow */}
              <div className="relative bg-slate-50 rounded-2xl p-8">
                <h4 className="text-lg font-bold text-slate-900 mb-6 text-center">
                  Optimized Claims Workflow
                </h4>
                <div className="space-y-4">
                  {claimsProcess.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-4"
                    >
                      <div className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 bg-white rounded-lg p-4 shadow-sm">
                        <div className="font-semibold text-slate-900 dark:text-white">{item.step}</div>
                        <div className="text-sm text-slate-500">{item.description}</div>
                      </div>
                      {index < claimsProcess.length - 1 && (
                        <div className="absolute left-[39px] mt-14 w-0.5 h-8 bg-sky-200" style={{ top: `${index * 76 + 24}px` }} />
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '35%', label: 'Faster Cycle Time' },
              { value: '50%', label: 'Less Manual Work' },
              { value: '20%', label: 'Improved Accuracy' },
              { value: '25%', label: 'Cost Reduction' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 text-sky-700 text-sm font-semibold rounded-full mb-4">
              Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Our ClaimCenter Expertise
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive claims solutions that improve efficiency and customer satisfaction.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <StaggerItem key={index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-14 h-14 rounded-xl bg-sky-50 flex items-center justify-center mb-6">
                    <capability.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {capability.description}
                  </p>
                  <ul className="space-y-2">
                    {capability.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-sky-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Transform Your Claims Operations?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Our ClaimCenter experts can help you reduce cycle times and improve 
              customer satisfaction with intelligent claims automation.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-8"
              >
                Request a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default ClaimCenter;
